import {afterNextRender, ApplicationRef, DOCUMENT, inject, Injectable, Injector} from "@angular/core";
import {ComponentPortal, DomPortalOutlet} from "@angular/cdk/portal";
import {DeliveryArticle} from "@knust/api-interfaces";
import {DeliveryLabelsComponent} from "./delivery-labels.component";

// Fallback, falls ein Browser nach dem Druckdialog kein afterprint-Event auslöst
const PRINT_CLEANUP_TIMEOUT = 60000;

@Injectable({
  providedIn: 'root'
})
export class DeliveryLabelsPrintService {
  private document = inject(DOCUMENT);
  private appRef = inject(ApplicationRef);
  private injector = inject(Injector);

  /**
   * Hängt die Etiketten direkt unter <body> ein, öffnet den Druckdialog und entfernt sie danach wieder.
   * Das Promise wird aufgelöst, sobald der Druckdialog geschlossen wurde.
   */
  print(articles: DeliveryArticle[]): Promise<void> {
    const printWindow = this.document.defaultView;

    if (!printWindow) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      const outlet = new DomPortalOutlet(this.document.body, this.appRef, this.injector);
      const componentRef = outlet.attach(new ComponentPortal(DeliveryLabelsComponent));
      componentRef.setInput('articles', articles);
      const hostElement: HTMLElement = componentRef.location.nativeElement;

      let cleanUpTimeout: ReturnType<typeof setTimeout>;
      const cleanUp = () => {
        clearTimeout(cleanUpTimeout);
        printWindow.removeEventListener('afterprint', cleanUp);
        // Nicht outlet.dispose() verwenden: Das entfernt das Outlet-Element selbst, also <body>
        outlet.detach();
        hostElement.remove();
        resolve();
      };

      // Erst drucken, wenn Angular die Etiketten gerendert hat
      afterNextRender(() => {
        printWindow.addEventListener('afterprint', cleanUp);
        cleanUpTimeout = setTimeout(cleanUp, PRINT_CLEANUP_TIMEOUT);
        printWindow.print();
      }, { injector: this.injector });
    });
  }
}
